import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle, HelpCircle, Shield, Newspaper, Users } from 'lucide-react';
import './ConclusionSlide.css';

// Import image
import protestImg from '../images/images for slides/fazit_protest.png.png';

function ConclusionSlide({ showNotes }) {
  const summaryPoints = [
    { icon: '🌐', text: 'Rechtsextremismus ist heute global vernetzt und digital professionell', color: '#ff3a3a' },
    { icon: '🧠', text: 'Die Strategie ist Metapolitik: Erst die Kultur, dann die Gesetze', color: '#ff6b35' },
    { icon: '🏛️', text: 'Sie greifen die Demokratie von innen an: Medien, Justiz, Institutionen', color: '#ffc107' },
    { icon: '💥', text: 'Ideologie führt zu realer Gewalt: Gaza, Schweden, Halle, Christchurch', color: '#3a7fff' },
    { icon: '🎭', text: 'Echte gesellschaftliche Probleme werden ausgenutzt, nicht gelöst', color: '#8b5cf6' },
  ];

  const hopePoints = [
    { icon: '🇵🇱', text: 'Polen hat gezeigt: Illiberale Regierungen können abgewählt werden' },
    { icon: '🇮🇱', text: 'Israelis protestieren gegen ihre eigene Regierung' },
    { icon: '📰', text: 'Zivilgesellschaft und freie Presse sind entscheidend' },
  ];

  return (
    <div className="slide conclusion-slide">
      <div className="conclusion-layout">
        {/* Header */}
        <div className="conclusion-header">
          <motion.span
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="section-label"
          >
            Fazit
          </motion.span>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="slide-title"
          >
            Zusammenfassung
          </motion.h2>
        </div>

        <div className="conclusion-content">
          {/* Left Column - Summary */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="summary-column"
          >
            <div className="summary-header">
              <AlertTriangle size={20} />
              <h3>Die Bedrohung</h3>
            </div>
            
            <div className="summary-list">
              {summaryPoints.map((point, index) => (
                <motion.div
                  key={index}
                  className="summary-item"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.08 }}
                  style={{ '--item-color': point.color }}
                >
                  <span className="summary-icon">{point.icon}</span>
                  <span className="summary-text">{point.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Column - Hope */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="hope-column"
          >
            <div className="hope-header">
              <CheckCircle size={20} />
              <h3>Aber: Demokratie kann sich wehren</h3>
            </div>
            
            <div className="hope-list">
              {hopePoints.map((point, index) => (
                <motion.div
                  key={index}
                  className="hope-item"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                >
                  <span className="hope-icon">{point.icon}</span>
                  <span className="hope-text">{point.text}</span>
                </motion.div>
              ))}
            </div>

            {/* Image placeholder */}
            <div className="hope-image">
              <img src={protestImg} alt="Demokratie Proteste" className="conclusion-content-image" />
            </div>

            {/* Key elements */}
            <div className="key-elements">
              <div className="element">
                <Shield size={18} />
                <span>Zivilgesellschaft</span>
              </div>
              <div className="element">
                <Newspaper size={18} />
                <span>Freie Presse</span>
              </div>
              <div className="element">
                <Users size={18} />
                <span>Aufklärung</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Big Question */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="big-question"
        >
          <div className="question-icon">
            <HelpCircle size={32} />
          </div>
          <div className="question-content">
            <span className="question-label">Die große Frage</span>
            <p className="question-text">
              "Muss eine Demokratie auch Meinungen aushalten, die sie abschaffen wollen?"
            </p>
          </div>
        </motion.div>
      </div>

      {showNotes && (
        <div className="speaker-notes">
          <strong>📚 Zusatzinfos für Leser (Zusammenfassung):</strong><br/>
          <strong>1. Professionalisierung:</strong> Die Szene ist global vernetzt und nutzt digitale Strategien.<br/>
          <strong>2. Metapolitik:</strong> Erst die Stimmung ändern, dann die Macht greifen.<br/>
          <strong>3. Staatsumbau:</strong> Regierende Rechtsextreme greifen Justiz und Medien an (Ungarn, Israel).<br/>
          <strong>4. Sprache & Gewalt:</strong> Radikale Sprache führt langfristig zu realer Gewalt.<br/><br/>
          <strong>Ausblick (Die Demokratie):</strong><br/>
          Demokratien sind widerstandsfähig. Polen hat den Wechsel geschafft, in Israel protestiert die Zivilgesellschaft.<br/>
          <strong>Die Abschlussfrage (Popper):</strong> "Ab welchem Punkt muss eine tolerante Gesellschaft Intoleranz begrenzen, um zu überleben?"
        </div>
      )}
    </div>
  );
}

export default ConclusionSlide;
